import {
  errorMessagesActions,
  apiCallFinished,
  errorTypes,
  errorMessages,
  errorPageActions,
} from '~/components';
import { setTokenToStorage } from '~/components/persist-token/persist-token.utils';
import { store } from '~/store';
import { getTimeSlotDescription } from '~/routes/utils/helpers';
import { api, appointmentDraftSelectors, tokenApi } from '~/api';
import { servicesSelectors } from '~/selectors';

const statusCodesToExcludeDispatchApiCallFinished = [401, 404, 409];
const responseMessagesToExcludeDispatchApiCallFinished = [
  'incompatible.appointment.state.exception',
];

export const handleResponse = (response) => {
  if (response.ok) {
    store.dispatch(errorMessagesActions.removeServerErrorMessage(response.url));
    return Promise.resolve(response);
  }
  if (
    !statusCodesToExcludeDispatchApiCallFinished.includes(response.status) &&
    !responseMessagesToExcludeDispatchApiCallFinished.includes(response.clone().json().code)
  ) {
    store.dispatch(apiCallFinished(true));
  }
  if (response.status >= 500 && response.status !== 503) {
    errorMessagesActions.pushServerErrorMessage(response.url, response.status)(
      store.dispatch,
      store.getState,
    );
  }
  return Promise.reject(response);
};

export const handleFetchError = (targetUrl) => {
  store.dispatch(apiCallFinished(true));
  errorMessagesActions.pushServerErrorMessage(targetUrl)(store.dispatch, store.getState);
  return Promise.reject();
};

export const handleJSONResponse = (response) => response.json();

const dispatchErrorMessage = (apiErrorMessageCode) => {
  store.dispatch(
    errorMessagesActions.resetErrorMessagesWith([
      {
        type: apiErrorMessageCode,
      },
    ]),
  );
};

const handleDoneStatusErrors = async (method, response) => {
  const json = await handleJSONResponse(response);
  const apiErrorMessageCode = `${method}.${json.code}`;

  switch (apiErrorMessageCode) {
    case errorTypes.VIEW_CANCELLED_BOOKING:
    case errorTypes.EDIT_CANCELLED_BOOKING:
      dispatchErrorMessage(apiErrorMessageCode);
      break;
    default:
      break;
  }
  return Promise.reject(response);
};

const handleUnprocessableEntityError = (response) =>
  handleJSONResponse(response.clone()).then((json) => {
    if (json.code === errorTypes.RESTORE_OUTDATED_APPOINTMENT_DRAFT) {
      return tokenApi.getInitialToken().then((token) => {
        setTokenToStorage(token);
        return Promise.resolve(response);
      });
    }
    if (json.code === errorTypes.BOOKING_BUG_CLIENT_ERROR) {
      store.dispatch(errorPageActions.setErrorStatus(503));
    }
    return Promise.reject(response);
  });

const getSlotNoLongerAvailableError = () => {
  const { APPOINTMENT_NO_LONGER_AVAILABLE_ABOUT_YOU_ERROR } = errorTypes;

  const {
    appointment: {
      draft: { dateSlot: selectedDateSlot, timeSlot: selectedTimeSlot },
    },
  } = store.getState();

  const messageTitle = getTimeSlotDescription(selectedDateSlot, selectedTimeSlot);
  const message = errorMessages[APPOINTMENT_NO_LONGER_AVAILABLE_ABOUT_YOU_ERROR];

  return errorMessagesActions.resetErrorMessagesWith([
    {
      type: APPOINTMENT_NO_LONGER_AVAILABLE_ABOUT_YOU_ERROR,
      title: message.createTitle(messageTitle),
    },
  ]);
};

const getAppointmentCantBeBookedWithinTimeIntervalError = () => {
  const { APPOINTMENT_CANT_BE_BOOKED_WITHIN_24_HOURS } = errorTypes;

  return errorMessagesActions.resetErrorMessagesWith([
    {
      type: APPOINTMENT_CANT_BE_BOOKED_WITHIN_24_HOURS,
      ...errorMessages[APPOINTMENT_CANT_BE_BOOKED_WITHIN_24_HOURS],
    },
  ]);
};

const resolveConflictErrorAction = (response) => {
  switch (response.code) {
    case 'fitted.bedrooms.booking.within.24.hours':
      store.dispatch(getAppointmentCantBeBookedWithinTimeIntervalError());
      break;
    case 'appointment.has.already.booked':
      store.dispatch(getSlotNoLongerAvailableError());
      break;
    default:
      break;
  }
};

const getConflictError = (response) => {
  handleJSONResponse(response)
    .then(resolveConflictErrorAction)
    .catch(() => {});
  store.dispatch(apiCallFinished(true));
  return Promise.reject(response);
};

const handleNotFoundErrorCode = (response) =>
  handleJSONResponse(response).then((data) => {
    switch (data.code) {
      case 'appointment.type.not.found': {
        const type = servicesSelectors.selectAppointmentTypeNotFound(store.getState());
        if (type) {
          store.dispatch(errorMessagesActions.resetErrorMessagesWith([{ type }]));
        }
        return Promise.reject(response);
      }
      case 'service.does.not.have.configured.partners': {
        const shopName = appointmentDraftSelectors.selectShop(store.getState()).name;
        const actionMessage = {
          type: errorTypes.NO_AVAILABLE_PARTNERS_ERROR,
          title: errorMessages[errorTypes.NO_AVAILABLE_PARTNERS_ERROR].createTitle(shopName),
        };
        store.dispatch(errorMessagesActions.resetErrorMessagesWith([actionMessage]));
        return Promise.reject(response);
      }
      default:
        store.dispatch(errorPageActions.setErrorStatus(404));
        return Promise.reject(response);
    }
  });

export const handleApiErrors =
  (method = 'GET', bypassErrorCodes = []) =>
  (response) => {
    if (!response || bypassErrorCodes.includes(response.status)) {
      return Promise.reject(response);
    }
    switch (response.status) {
      case 400:
        return handleJSONResponse(response)
          .then(({ code }) => {
            if (code === 'token.invalid') {
              if (store.getState().services.config.firstPageNotRequiredToken) {
                store.dispatch(
                  errorMessagesActions.resetErrorMessagesWith([
                    {
                      type: errorTypes.INVALID_TOKEN_ERROR_GO_TO_BEGIN_OF_JOURNEY,
                      ...errorMessages[errorTypes.INVALID_TOKEN_ERROR_GO_TO_BEGIN_OF_JOURNEY],
                    },
                  ]),
                );
              } else {
                store.dispatch(
                  errorMessagesActions.resetErrorMessagesWith([
                    {
                      type: errorTypes.INVALID_TOKEN_ERROR,
                      ...errorMessages[errorTypes.INVALID_TOKEN_ERROR],
                    },
                  ]),
                );
              }
            }
          })
          .then(() => Promise.reject(response));
      case 401:
        window.location = api.getSignInUrl();
        return Promise.reject(response);
      case 403:
        store.dispatch(
          errorMessagesActions.resetErrorMessagesWith([
            {
              type: errorTypes.FORBIDDEN_ERROR,
              ...errorMessages[errorTypes.FORBIDDEN_ERROR],
            },
          ]),
        );
        return Promise.reject(response);
      case 404:
        return handleNotFoundErrorCode(response);
      case 409:
        return getConflictError(response);
      case 410:
        return handleDoneStatusErrors(method, response);
      case 422:
        return handleUnprocessableEntityError(response);
      case 503:
        store.dispatch(errorPageActions.setErrorStatus(503));
        return Promise.reject(response);
      default:
        return Promise.reject(response);
    }
  };
